import type { ReactNode } from 'react';

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="mt-2 text-base font-semibold text-fg">{children}</h3>;
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-fg-secondary">{children}</p>;
}

export function UL({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-fg-secondary">{children}</ul>;
}

export function OL({ children }: { children: ReactNode }) {
  return <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-fg-secondary">{children}</ol>;
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-control border border-info/30 bg-info-subtle px-3 py-2 text-sm text-fg-secondary">{children}</div>
  );
}

export function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-card border border-line">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-2 text-fg">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line text-fg-secondary">
          {rows.map((row, i) => (
            <tr key={i} className="align-top">
              {row.map((cell, j) => (
                <td key={j} className={`px-3 py-2 ${j === 0 ? 'font-medium text-fg' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const Menu = ({ children }: { children: ReactNode }) => <span className="font-medium text-fg">{children}</span>;

/** A screenshot of the real application with a caption. Images live in frontend/public/guide. */
export function Figure({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="flex flex-col gap-1.5">
      <img src={src} alt={alt} loading="lazy" className="w-full rounded-card border border-line shadow-card" />
      <figcaption className="text-xs text-fg-muted">{caption}</figcaption>
    </figure>
  );
}

/** Frequently asked questions of one audience, as folding rows. */
export function Faq({ items }: { items: [string, string][] }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map(([question, answer]) => (
        <details key={question} className="group rounded-card border border-line bg-surface-1 px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-fg marker:text-fg-muted">{question}</summary>
          <p className="mt-2 text-sm leading-relaxed text-fg-secondary">{answer}</p>
        </details>
      ))}
    </div>
  );
}
